import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Loader2, Save, Ruler, Layers } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm text-slate-900 focus:border-spike-blue focus:ring-2 focus:ring-spike-blue/20 outline-none";
const labelCls = "block text-[0.65rem] font-semibold text-slate-500 uppercase tracking-wider mb-1";

const SURFACES = [
  { id: "", label: "—" },
  { id: "turf", label: "Turf" },
  { id: "matting", label: "Matting" },
  { id: "concrete", label: "Concrete" },
  { id: "synthetic", label: "Synthetic" },
];

const LaneRow = ({ lane, machines, onSaved }) => {
  const [draft, setDraft] = useState({
    length_ft: lane.length_ft ?? "",
    width_ft: lane.width_ft ?? "",
    surface: lane.surface || "",
    supported_machine_ids: lane.supported_machine_ids || [],
    notes: lane.notes || "",
  });
  const [busy, setBusy] = useState(false);

  // Re-sync if parent reloads
  useEffect(() => {
    setDraft({
      length_ft: lane.length_ft ?? "",
      width_ft: lane.width_ft ?? "",
      surface: lane.surface || "",
      supported_machine_ids: lane.supported_machine_ids || [],
      notes: lane.notes || "",
    });
  }, [lane]);

  const toggleMachine = (id) => {
    setDraft((d) => ({
      ...d,
      supported_machine_ids: d.supported_machine_ids.includes(id)
        ? d.supported_machine_ids.filter((x) => x !== id)
        : [...d.supported_machine_ids, id],
    }));
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(
        `/admin/venues/${lane.venue_id}/lanes/${lane.lane_number}`,
        {
          length_ft: draft.length_ft === "" ? null : Number(draft.length_ft),
          width_ft: draft.width_ft === "" ? null : Number(draft.width_ft),
          surface: draft.surface || null,
          supported_machine_ids: draft.supported_machine_ids,
          notes: draft.notes,
        },
        { withCredentials: true }
      );
      toast.success(`Lane ${lane.lane_number} saved.`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-card" data-testid={`lane-row-${lane.lane_number}`}>
      <div className="flex flex-wrap items-start gap-5">
        <div className="w-16 h-16 bg-spike-blue text-white font-bold text-xl flex items-center justify-center rounded shrink-0">
          {lane.lane_number}
        </div>
        <div className="flex-1 min-w-[260px] grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>Length (ft)</label>
            <input
              type="number"
              step="0.1"
              data-testid={`lane-length-${lane.lane_number}`}
              className={inputCls}
              value={draft.length_ft}
              onChange={(e) => setDraft({ ...draft, length_ft: e.target.value })}
              placeholder="e.g. 66"
            />
          </div>
          <div>
            <label className={labelCls}>Width (ft)</label>
            <input
              type="number"
              step="0.1"
              data-testid={`lane-width-${lane.lane_number}`}
              className={inputCls}
              value={draft.width_ft}
              onChange={(e) => setDraft({ ...draft, width_ft: e.target.value })}
              placeholder="e.g. 10"
            />
          </div>
          <div>
            <label className={labelCls}>Surface</label>
            <select
              data-testid={`lane-surface-${lane.lane_number}`}
              className={inputCls}
              value={draft.surface}
              onChange={(e) => setDraft({ ...draft, surface: e.target.value })}
            >
              {SURFACES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div className="col-span-2 sm:col-span-3">
            <label className={labelCls}>Supported machines</label>
            <div className="flex flex-wrap gap-2">
              {machines.length === 0 && (
                <span className="text-xs text-slate-400">No machines configured. Add machines in the Machines section first.</span>
              )}
              {machines.map((m) => {
                const on = draft.supported_machine_ids.includes(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    data-testid={`lane-${lane.lane_number}-machine-${m.id}`}
                    onClick={() => toggleMachine(m.id)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      on
                        ? "bg-spike-blue border-spike-blue text-white"
                        : "bg-white border-slate-300 text-slate-700 hover:border-spike-blue"
                    }`}
                  >
                    {m.name} <span className="opacity-60">· {m.sport}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="col-span-2 sm:col-span-3">
            <label className={labelCls}>Notes</label>
            <input
              data-testid={`lane-notes-${lane.lane_number}`}
              className={inputCls}
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              placeholder="Internal notes (visible on booking page)"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          data-testid={`lane-save-${lane.lane_number}`}
          className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-md shrink-0"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
      </div>
    </div>
  );
};

const AdminLanes = () => {
  const [venues, setVenues] = useState([]);
  const [venueId, setVenueId] = useState("mississauga");
  const [lanes, setLanes] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);

  const venue = useMemo(() => venues.find((v) => v.id === venueId), [venues, venueId]);

  useEffect(() => {
    (async () => {
      try {
        const [v, m] = await Promise.all([
          api.get("/venues"),
          api.get("/admin/machines", { withCredentials: true }),
        ]);
        setVenues(v.data);
        setMachines(m.data);
        if (v.data.length) {
          setVenueId((current) =>
            v.data.find((x) => x.id === current) ? current : v.data[0].id
          );
        }
      } catch {
        toast.error("Could not load venues / machines.");
      }
    })();
  }, []);

  const reload = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/venues/${venueId}/lanes`, { withCredentials: true });
      setLanes(data);
    } catch {
      toast.error("Could not load lanes.");
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="space-y-5" data-testid="admin-lanes">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Ruler className="w-5 h-5 text-spike-blue" /> Lane Metadata
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Per-lane dimensions, surface and supported machines. Shown on the public booking page when a lane is selected.
          </p>
        </div>
        <select
          data-testid="lanes-venue-select"
          value={venueId}
          onChange={(e) => setVenueId(e.target.value)}
          className="bg-white border border-slate-300 rounded-md px-3 py-2 text-sm"
        >
          {venues.map((v) => (
            <option key={v.id} value={v.id}>{v.name}</option>
          ))}
        </select>
      </div>

      {venue && (
        <div className="bg-spike-blue-pale text-spike-blue text-sm rounded-md px-4 py-3 flex items-center gap-2">
          <Layers className="w-4 h-4" />
          <span>
            Venue has <strong>{venue.lanes}</strong> lanes. Edit each below — empty fields are simply hidden on the booking page.
          </span>
        </div>
      )}

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-spike-blue" /></div>
      ) : (
        <div className="space-y-3">
          {lanes.map((l) => (
            <LaneRow key={l.lane_number} lane={l} machines={machines} onSaved={reload} />
          ))}
          {lanes.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500" data-testid="lanes-empty">
              No lanes — set venue lane count first.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminLanes;
